import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../redux/store";
// Make sure getStats accepts params in your definition file, e.g., getStats(params?: {fromDate?: string, toDate?: string})
import { dashboardApi, DashboardStats } from "../../api/dashboard.api";
import { Alert } from "../common/Alert";

// Assume this is your currency code helper
const formatCurrency = (value: number) => `₹ ${value.toLocaleString("en-IN")}`;

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Date filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Used to trigger the useEffect when the user clicks "Apply"
  const [filterApplied, setFilterApplied] = useState(0);

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      // We don't necessarily want to clear success immediately if it's still showing from a previous action
      // setSuccess(null); 

      // Prepare query parameters based on selected dates
      const params: Record<string, string> = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      // FIX: Pass the date filters to the API call
      const response = await dashboardApi.getStats(params);
      
      setStats(response.data.data);
      setSuccess('Dashboard data loaded successfully');
      // Clear the success message after a delay
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to load dashboard data"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterApplied]); // Only re-run when filterApplied counter changes

  // FIX: Added missing handler for the filter button
  const handleApplyFilter = () => {
    // Incrementing this value triggers the useEffect to re-fetch
    setFilterApplied(prev => prev + 1);
  };

  const handleStatClick = (statType: string, count: number) => {
    // Don't navigate if the count is 0 (unless it's the value card which passes 1)
    if (count <= 0 && statType !== 'totalAssetValue') return;

    switch (statType) {
      case 'totalAssets':
        navigate('/assets');
        break;
      case 'totalUsers':
        navigate('/users');
        break;
      case 'expiringSoon':
        navigate('/assets?filter=EXPIRED_SOON');
        break;
      case 'expired':
        navigate('/assets?filter=EXPIRED');
        break;
      case 'assigned':
        navigate('/assets?filter=ASSIGNED');
        break;
      case 'nonAssigned':
        navigate('/assets?filter=UNASSIGNED');
        break;
      case 'totalAssetCurrentValue':
         navigate('/assets'); // Or wherever this should lead
         break;
      default:
        break;
    }
  };


  const StatCard: React.FC<{
    title: string;
    value: number;
    statType: string;
  }> = ({ title, value, statType }) => {
    const isClickable = value > 0;

    return (
      <div
        className={`bg-white p-4 rounded-lg shadow-md ${
          isClickable
            ? 'cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200'
            : 'cursor-default'
        }`}
        onClick={() => handleStatClick(statType, value)}
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {title}
        </h2>
        <p className={`text-3xl font-bold ${isClickable ? 'text-primary' : 'text-gray-400'}`}>
          {value}
        </p>
      </div>
    );
  };

  // 2. Value Card for the Right Column (GREEN from the image)
  const ValueInRightCard: React.FC<{
    title: string;
    value: string;
    statType: string;
  }> = ({ title, value, statType }) => {
    // passing 1 as count just to ensure it passes the click handler check
    return (
      <div
        onClick={() => handleStatClick(statType, 1)}
        className="bg-blue-500 p-6 rounded-lg shadow-xl cursor-pointer hover:bg-blue-300 transition text-white"
      >
        <p className="text-sm uppercase tracking-wider font-medium opacity-90">{title}</p>
        <p className="text-4xl font-bold mt-2">
          {value}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      
      {/* --- HEADER & FILTER --- */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        
        {/* Dashboard Title */}
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

        {/* Date Filter UI */}
        <div className="flex flex-wrap gap-2 mt-4 sm:mt-0 bg-white p-3 rounded-lg border shadow-sm">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="From Date"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="To Date"
          />
          <button
            onClick={handleApplyFilter}
            className="bg-gray-800 text-white px-3 py-1 rounded text-sm hover:bg-gray-900 transition"
          >
            Apply Filter
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6">
          <Alert type="error" message={error} />
        </div>
      )}
      {success && (
        <div className="mb-6">
          <Alert type="success" message={success} />
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* --- LEFT SECTION: MAIN METRICS (3/4 width) --- */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 auto-rows-max">
            
            {/* Standard Stat Cards */}
              <StatCard
                title="Total Assets"
                value={stats.totalAssets}
                statType="totalAssets"
              />

            <StatCard
              title="Total Users"
              value={stats.totalUsers}
              statType="totalUsers"
            />
            
            {/* Admin-only Cards */}
            {user?.role === "ADMIN" && (
              <React.Fragment>
                <StatCard
                  title="Expiring Soon Assets"
                  value={stats.expiringSoonCount}
                  statType="expiringSoon"
                />
                <StatCard
                  title="Expired Assets"
                  value={stats.expiredAssets}
                  statType="expired"
                />
                <StatCard
                  title="Assigned Assets"
                  value={stats.assignedAssets}
                  statType="assigned"
                />
                <StatCard
                  title="Non Assigned Assets"
                  value={stats.nonAssignedAssets}
                  statType="nonAssigned"
                />
              </React.Fragment>
            )}
          </div>

          {/* --- RIGHT SECTION: VALUE & CATEGORY PANEL (1/4 width) --- */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Total Asset Value - GREEN CARD */}
            {/* NOTE: Multiplying by 10000 is likely placeholder logic. Ideally, the API provides the total value. */}
            <ValueInRightCard
              title=" ASSET Current VALUE"
              value={formatCurrency(stats.totalAssetCurrentValue ?? 0)}
              statType="totalAssetCurrentValue"
            />
            {/* CATEGORY PANEL */}
            <div className="bg-white p-6 rounded-lg shadow-md h-fit border border-gray-100">
  {/* FIX: Corrected text color and removed invalid 'center' class. Added font-bold. */}
  <div className="flex items-center justify-between mb-6">
    <h3 className="text-lg font-bold text-gray-900">
      Assets by Category
    </h3>
    {/* Optional: Added total count badge to header */}
    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
       {stats.categoryWise.length} Total
    </span>
  </div>


  {/* IMPROVEMENT: Made placeholder look intentional with icon and dashed border */}
  <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200 mb-6">
    
  </div>

  <ul className="flex flex-col divide-y divide-gray-100">
    {stats.categoryWise.map((cat, index) => (
      <li
        key={cat.category}
        // IMPROVEMENT: Added hover effect and slight negative margin/padding for full-width hover
        className="flex justify-between items-center py-3 px-2 -mx-2 hover:bg-gray-50 rounded-md transition-colors"
      >
        <div className="flex items-center">
             {/* Optional: Add index number for a cleaner list look */}
            <span className="text-xs text-gray-400 mr-3 w-4">{index + 1}.</span>
            <span className="text-gray-700 font-medium truncate" title={cat.category}>
                {cat.category}
            </span>
        </div>

        {/* IMPROVEMENT: A cleaner, more professional pill style for counts */}
        <span className="inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-semibold leading-none text-gray-600 bg-gray-100 rounded-full border border-gray-200">
          {cat.count}
        </span>
      </li>
    ))}
  </ul>
</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;